/**
 * SDK 硬件缓存层 - ReportBackfill (Phase 4)
 *
 * 监听 `BaseDevice` 的 `report:*` 事件,把硬件主动上报的字段回填到缓存,
 * 让对应的 `getXxx()` 调用直接命中、不再触发硬件读。
 *
 * 本组件**不解析任何业务字段**,解析逻辑仍归驱动自己,
 * `ReportBackfill` 只是一个"事件 → (key, bytes) 的映射执行器"。
 *
 * --- 关键约束 ---
 *
 * 1. **同一 event 多条规则共用一个监听器** (Requirement 4.5):
 *    内部按数组顺序依次执行,任意一条失败不阻塞其他规则。
 *
 * 2. **extract 异常隔离** (Requirement 4.4):
 *    每条规则的 `extract` 调用包 `try/catch`,
 *    异常时 `console.warn` 输出 event 名 + 规则索引 + 错误,继续后续规则。
 *
 * 3. **stop() 解除所有监听** (Requirement 4.6):
 *    调用后上报不再触发回填。
 *
 * 4. **回填条目携带 meta** (Requirement 4.3):
 *    把 `BackfillItem` 中的 `tags` / `addressStart` / `length` 透传给
 *    `cache.set` 的 meta,使回填条目同样可被 Phase 3 的 tag/区间失效命中。
 *
 * 5. **缓存 key 与 sendRead 对齐**:
 *    规则 `extract` 产出的 `BackfillItem.key` 为 `makeKey(address, length)`;
 *    写入时由本组件补上 `${device.id}:` 前缀,与 `createSendRead` 的查找 key 一致。
 *
 * 6. **缓存被 disable 时仍调 extract** (Requirement 4.9):
 *    `cache.set` 静默丢弃由 TaggedCache/HardwareCache 的 disable 状态机自然处理。
 *
 * 7. **防御性 try/catch** (Requirement 5.8, 5.9):
 *    公开方法最外层包 try/catch,不向调用栈传播异常。
 *
 * @packageDocumentation
 */

import type { ReportBackfillRule } from './types'

/**
 * 缓存接口 - ReportBackfill 只依赖 `set` 方法,
 * 兼容 TaggedCache 与 HardwareCache。
 */
interface CacheWriter {
  set(
    key: string,
    data: Uint8Array,
    meta?: { tags?: string[]; addressStart?: number; length?: number },
  ): void
}

/**
 * 事件发射器接口 - 仅需 on / off 能力。
 */
interface EventSource {
  on(event: string, listener: (...args: any[]) => void): any
  off(event: string, listener: (...args: any[]) => void): any
}

/**
 * 设备接口 - ReportBackfill 只依赖事件订阅、设备 id 与 cache 字段。
 * `id` 用于与 `sendRead` 对齐缓存 key 前缀 (`${device.id}:${makeKey(...)}`)。
 */
interface BackfillDevice extends EventSource {
  id: string
  cache: CacheWriter
}

/**
 * Phase 4 上报数据回填规则引擎。
 *
 * 典型用法 (在驱动构造函数末尾):
 * ```ts
 * const backfill = new ReportBackfill(this, CH585M_REPORT_RULES)
 * backfill.start()
 * ```
 *
 * 详见:
 * - design.md `Components §4` (接口契约)
 * - requirements.md 4.1~4.9
 */
export class ReportBackfill {
  /**
   * event → 绑定的监听器函数,用于 stop() 时解除订阅。
   */
  private readonly listeners = new Map<string, (payload: unknown) => void>()

  /**
   * 是否已启动。防止重复 start。
   */
  private started = false

  constructor(
    private readonly device: BackfillDevice,
    private readonly rules: ReportBackfillRule[],
  ) {}

  /**
   * 订阅所有规则中声明的 event,开始回填。
   * 通常在驱动构造函数末尾调用一次。
   *
   * 同一 event 多条规则共用一个监听器,内部按数组顺序依次执行。
   */
  start(): void {
    try {
      if (this.started) return

      // 按 event 分组规则
      const rulesByEvent = new Map<string, Array<{ rule: ReportBackfillRule; index: number }>>()

      for (let i = 0; i < this.rules.length; i++) {
        const rule = this.rules[i]
        let group = rulesByEvent.get(rule.event)
        if (!group) {
          group = []
          rulesByEvent.set(rule.event, group)
        }
        group.push({ rule, index: i })
      }

      // 为每个独特的 event 注册一个监听器
      for (const [event, group] of rulesByEvent) {
        const listener = (payload: unknown) => {
          this.handleEvent(event, group, payload)
        }
        this.listeners.set(event, listener)
        this.device.on(event, listener)
      }

      this.started = true
    } catch (err) {
      console.error('[ReportBackfill] start() internal error:', err)
    }
  }

  /**
   * 取消订阅,清理事件监听器。后续上报不再触发回填。
   * 一般在 close / disconnect 时调用。
   */
  stop(): void {
    try {
      for (const [event, listener] of this.listeners) {
        this.device.off(event, listener)
      }
      this.listeners.clear()
      this.started = false
    } catch (err) {
      console.error('[ReportBackfill] stop() internal error:', err)
    }
  }

  /**
   * 处理单个事件的所有规则。按数组声明顺序依次执行,
   * 单条 extract 抛错不影响其他规则。
   */
  private handleEvent(
    event: string,
    group: Array<{ rule: ReportBackfillRule; index: number }>,
    payload: unknown,
  ): void {
    for (const { rule, index } of group) {
      try {
        const items = rule.extract(payload)
        if (!items || items.length === 0) continue

        for (const item of items) {
          // 与 createSendRead 的 key 格式保持一致,否则回填永远无法被后续读命中
          const key = `${this.device.id}:${item.key}`
          this.device.cache.set(key, item.data, {
            tags: item.tags,
            addressStart: item.addressStart,
            length: item.length,
          })
        }
      } catch (err) {
        console.warn(`[ReportBackfill] Rule[${index}] extract failed for event "${event}":`, err)
        // 继续执行后续规则,不取消订阅 (Requirement 4.4)
      }
    }
  }
}
